import { useAuth } from "@/contexts/AuthContext";
import { useAPI } from "@/hooks/useAPI";
import { canAssignAnalysts } from "@/permissions/firms";
import { type Analyst, UsersService } from "@/services/usersService";
import { getErrorMessage } from "@/utils/errorUtils";
import useSWR from "swr";

export const useAnalysts = (allowSuperAnalyst: boolean = true) => {
  const api = useAPI("v2");
  const { firmContext } = useAuth();
  const usersService = new UsersService(api);

  const canFetchAnalysts = canAssignAnalysts(firmContext);
  const analystsKey =
    firmContext?.firmId && canFetchAnalysts
      ? `/users/${firmContext.firmId}/analysts?allow_super_analyst=${allowSuperAnalyst}`
      : null;

  const { data, error, isLoading, mutate } = useSWR<Analyst[]>(
    analystsKey,
    () => {
      if (!firmContext?.firmId) {
        throw new Error("Firm context is not available");
      }
      return usersService.getAnalysts(firmContext.firmId, allowSuperAnalyst);
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 1 * 24 * 60 * 60 * 1000, // 1 day
    }
  );

  return {
    analysts: data || [],
    isLoading,
    isError: Boolean(error),
    ErrorMessage: getErrorMessage(error),
    refetch: () => mutate(),
  };
};
